import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SingleLocationsFilter } from '~/components/Filters/LocationsFilter/SingleLocationsFilter';
import { FilterTrigger } from '~/components/Filters/SingleFilters/FilterTrigger';
import { type SingleFilterProps } from '~/components/Filters/types';
import { baseFiltersOptions } from '~/components/FiltersMenu/types';
import { useLibrary } from '~/components/LibraryBeta/hooks/queries/useLibrary';
import { Routes } from '~/constants/routes';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { removeFilterAction } from '~/store/filters/actions';
import { type SelectedFilter } from '~/store/filters/types';
import { pushWithExistingQuery } from '~/utils/PathUtils';

export type LocationsFilterTriggerProps = SingleFilterProps;

export const LocationsFilterTrigger = memo(
  ({ onRemoveClick, onApplyClick, onOpenChange }: LocationsFilterTriggerProps) => {
    const dispatch = useDispatch();
    const { locationsParam } = useFilterParams();
    const libraryId = useURLLibraryIdSelector();
    const { data: library } = useLibrary({ libraryId });
    const board = useSelector(centralizedBoardSelector);

    const content = useMemo(() => <SingleLocationsFilter onApplyClick={onApplyClick} />, [onApplyClick]);

    const paramFiltersCount = useMemo(() => {
      const boards = locationsParam?.boards?.or ?? [];
      const libraries = locationsParam?.libraries?.or ?? [];
      return [...boards, ...libraries].length;
    }, [locationsParam]);

    const values = useMemo(() => {
      if (library) {
        return [library.title];
      }

      if (board) {
        return [board.title];
      }

      return [];
    }, [board, library]);

    const count = useMemo(() => {
      // Don't add values.length if the current board/library is already in the filters
      // Check if current board is in the selected boards
      const currentBoardInFilters = board && locationsParam?.boards?.or?.includes(board.id);
      // Check if current library is in the selected libraries
      const currentLibraryInFilters = library && locationsParam?.libraries?.or?.includes(library.id);

      // Only add values.length if the current board/library is NOT already counted in paramFiltersCount
      const shouldAddCurrentContext = values.length > 0 && !currentBoardInFilters && !currentLibraryInFilters;

      if (shouldAddCurrentContext) {
        return paramFiltersCount + values.length;
      }
      return paramFiltersCount;
    }, [paramFiltersCount, values, board, library, locationsParam]);

    const removeFilter = useCallback(async () => {
      const filter: SelectedFilter = { type: 'locations' };
      dispatch(removeFilterAction({ filter }));
      onRemoveClick?.([filter]);

      /**
       * If the filter is removed and `library` or `board` is present, we need to push the user to
       * all search page.
       */
      if (library || board) {
        await pushWithExistingQuery({
          path: Routes.media.all,
        });
      }
    }, [dispatch, onRemoveClick, library, board]);

    return (
      <FilterTrigger
        count={count}
        onRemoveClick={removeFilter}
        filterContent={content}
        title="Locations"
        Icon={baseFiltersOptions['locations'].icon}
        values={!paramFiltersCount ? values : undefined}
        onOpenChange={onOpenChange}
      />
    );
  },
);

LocationsFilterTrigger.displayName = 'LocationsFilterTrigger';
